import React from 'react';
import styled from 'styled-components';
import { Button, Label, LeftIconWrap, LabelWrap, RightIconWrap, Content } from '../elements/Button';
import TriggerPopupSmall from '../elements/TriggerPopupSmall';
import { ICON_PENCIL_MAGIC } from '../refs/Icons';
import { getColor } from '../utils/styled';
import { PURE_WHITE, GRAY_40, SILVER_CHALICE, GRAY_120, LOCHMARA } from '../refs/Colors';
import {Toggleable} from "@/types/Toggleable.ts";

type ReportToolbarProps = {
  className?: string;
  children?: React.ReactNode;
  withoutAI?: boolean;
  buttonIcon?: string;
  selectorContent: string;
  templateMenu: Toggleable;
  onClickButton: () => void;
};

const ToolbarWrap = styled.div<{$withoutAI?: boolean}>`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  background-color: ${getColor(PURE_WHITE)};
  border: 1px solid ${getColor(GRAY_40)};
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 210mm;
  margin: 0 auto -1px auto;
  z-index: 5;
  position: relative;

  ${({$withoutAI}) => $withoutAI && `
    grid-template-columns: repeat(3, minmax(0, 1fr));
    padding-left: 16px;
    padding-right: 8px;
  `};

  @media (min-width: 600px) {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    column-gap: 24px;
  }

`;

const LeftSlot = styled.div`
  justify-self: start;
  display: inline-flex;
`;

const CenterSlot = styled.div<{$withoutAI?: boolean}>`
  justify-self: center;
  display: inline-flex;
  align-items: center;

  ${({$withoutAI}) => $withoutAI && `
    justify-self: start;
  `}
`;

const CenterText = styled.p<{$withoutAI?: boolean}>`
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 11px;
  line-height: 1;
  color: ${getColor(SILVER_CHALICE)};
  text-align: center;
  margin-bottom: 0;

  ${({$withoutAI}) => $withoutAI && `
    text-align: left;
  `}
`;

const RightSlot = styled.div<{withoutAI?: boolean}>`
  justify-self: end;
  display: inline-flex;
  align-items: center;
  gap: 1px;

  ${({withoutAI}) => withoutAI && `
    grid-column: 3;
  `}
`;

const SelectorLabel = styled.span`
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 11px;
  line-height: 1.1em;
  color: ${getColor(GRAY_120)};
  margin-right: 4px;
`;

const ToolbarButton = styled(Button).attrs({ variant: 'text' })`
  && {
    height: 28px;
    gap: 6px;
    padding: 0;
    border-radius: 4px;
  }
  
  /* Compact inner paddings for toolbar scale */
  ${Content} ${LeftIconWrap} {
    padding-left: 0;
  }
  ${Content} ${LabelWrap} {
    padding: 0;
  }
  ${RightIconWrap} {
    padding-right: 0;
  }
  
  /* Label sizing: match toolbar text scale; color comes from prop */
  ${Label} {
    font-size: 12px;
  }
`;

export const ReportToolbar: React.FC<ReportToolbarProps> = ({
  className,
  children,
  withoutAI,
  buttonIcon = ICON_PENCIL_MAGIC,
  selectorContent,
  templateMenu,
  onClickButton
}) => {
  return (
    <ToolbarWrap className={className} $withoutAI={withoutAI}>
      {
        !withoutAI && <LeftSlot>
          <ToolbarButton icon={buttonIcon} onClick={onClickButton} color={LOCHMARA} data-testid='ai-compose'/>
        </LeftSlot>
      }
      <CenterSlot $withoutAI={withoutAI}>
        <CenterText $withoutAI={withoutAI}>{children}</CenterText>
      </CenterSlot>
      <RightSlot withoutAI={withoutAI}>
        <SelectorLabel>Template:</SelectorLabel>
        <TriggerPopupSmall toggleable={templateMenu} data-testid='template-select'>{selectorContent}</TriggerPopupSmall>
      </RightSlot>
    </ToolbarWrap>
  );
};
